import { useSyncExternalStore } from "react";
import {
  createWatchlistStore,
  type KeyValueStorage,
  type WatchlistDoc,
  type WatchlistStore,
} from "@helm/core";

/**
 * The window's watchlists (`watchlist/main`, synced per user; pure rules in @helm/core
 * watchlists/). The right-sidebar Watchlist tab and the Watchlist module read the same store, so
 * they always show the same lists. The dev preview switches to a local-only copy before anything
 * renders (`localOnlyWatchlistStore`).
 */

const SYNCED_KEY = "helm:watchlists";
const PREVIEW_KEY = "helm:watchlists:preview";

const localStore: KeyValueStorage = {
  get: (key) => {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key, value) => {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Quota / private mode: lists simply aren't cached locally.
    }
  },
};

let preview = false;
let store: WatchlistStore | null = null;

/** Dev preview: lists stay in local storage, never the API. Call before first render. */
export function localOnlyWatchlistStore(): void {
  preview = true;
}

export function watchlistStore(): WatchlistStore {
  if (store === null) {
    store = createWatchlistStore({
      storageKey: preview ? PREVIEW_KEY : SYNCED_KEY,
      storage: localStore,
      sync: !preview,
    });
    // Loads once per window (idempotent); the mobile Markets tab has no shell hook of its own.
    void store.start();
  }
  return store;
}

/** Route a watchlist `DocChanged` (session.ts) to the store. */
export function watchlistDocChanged(docId: string, version: number): void {
  if (docId !== "main" || store === null) return;
  void store.refresh(version);
}

export function useWatchlistDoc(): WatchlistDoc {
  const s = watchlistStore();
  return useSyncExternalStore(s.subscribe, s.getSnapshot);
}

export function newListId(): string {
  return `wl-${crypto.randomUUID().slice(0, 8)}`;
}

export function newSectionId(): string {
  return `sec-${crypto.randomUUID().slice(0, 8)}`;
}
